import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown, Mic } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ASSETS } from "@/lib/assets";

const NAV = [
  { label: "Home", to: "/" },
  { label: "About", to: "/about" },
  { label: "Events", to: "/events" },
  { label: "Messages", to: "/archives" },
  { label: "Gallery", to: "/gallery" },
  {
    label: "Connect",
    children: [
      { label: "Prayer Requests", to: "/prayer" },
      { label: "Contact", to: "/contact" },
    ],
  },
  { label: "Blog", to: "/blog" },
  { label: "Give", to: "/give" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 border-b ${
        scrolled ? "bg-black/70 backdrop-blur-xl border-white/10" : "bg-transparent border-transparent"
      }`}
    >
      <nav className="mx-auto max-w-[1400px] px-5 sm:px-8 h-20 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
          <img src={ASSETS.logoIcon} alt="WUTN" className="h-11 w-11 object-contain" />
          <span className="hidden sm:block font-cond font-bold uppercase tracking-[0.18em] text-sm text-white leading-none">
            Wake-Up<br /><span className="text-wutn">The Nation</span>
          </span>
        </Link>

        {/* Desktop nav */}
        <div className="hidden lg:flex items-center gap-1">
          {NAV.map((item) =>
            item.children ? (
              <DropdownMenu key={item.label}>
                <DropdownMenuTrigger
                  data-testid={`nav-${item.label.toLowerCase()}-trigger`}
                  className="flex items-center gap-1 px-4 py-2 font-cond uppercase tracking-wider text-sm text-zinc-300 hover:text-white transition-colors outline-none"
                >
                  {item.label}
                  <ChevronDown className="h-3.5 w-3.5" />
                </DropdownMenuTrigger>
                <DropdownMenuContent className="bg-black border border-white/10 rounded-none min-w-[200px]">
                  {item.children.map((c) => (
                    <DropdownMenuItem key={c.to} asChild className="rounded-none focus:bg-wutn focus:text-white cursor-pointer">
                      <Link to={c.to} data-testid={`nav-link-${c.to.slice(1)}`} className="font-cond uppercase tracking-wider text-sm py-2.5">
                        {c.label}
                      </Link>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`nav-link-${item.to === "/" ? "home" : item.to.slice(1)}`}
                className={({ isActive }) =>
                  `px-4 py-2 font-cond uppercase tracking-wider text-sm transition-colors ${
                    isActive ? "text-wutn" : "text-zinc-300 hover:text-white"
                  }`
                }
              >
                {item.label}
              </NavLink>
            )
          )}
          <Link
            to="/book"
            data-testid="nav-book-cta"
            className="ml-3 inline-flex items-center gap-2 bg-wutn hover:bg-wutn-dark text-white font-cond uppercase tracking-wider text-sm px-5 py-2.5 transition-colors"
          >
            <Mic className="h-4 w-4" /> Book Me
          </Link>
        </div>

        {/* Mobile toggle */}
        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen(true)}
          className="lg:hidden text-white p-2"
          aria-label="Open menu"
        >
          <Menu className="h-7 w-7" />
        </button>
      </nav>

      {/* Mobile drawer */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black lg:hidden"
            data-testid="mobile-menu"
          >
            <div className="flex items-center justify-between h-20 px-5 border-b border-white/10">
              <img src={ASSETS.logoIcon} alt="WUTN" className="h-11 w-11 object-contain" />
              <button data-testid="mobile-menu-close" onClick={() => setOpen(false)} className="text-white p-2">
                <X className="h-7 w-7" />
              </button>
            </div>
            <div className="px-6 py-8 flex flex-col gap-1 overflow-y-auto h-[calc(100vh-5rem)]">
              {NAV.flatMap((item) =>
                item.children
                  ? [
                      <span key={item.label} className="mt-5 mb-1 text-xs uppercase tracking-[0.25em] text-wutn font-bold">
                        {item.label}
                      </span>,
                      ...item.children.map((c) => (
                        <MobileLink key={c.to} to={c.to} onClick={() => setOpen(false)} label={c.label} indent />
                      )),
                    ]
                  : [<MobileLink key={item.to} to={item.to} onClick={() => setOpen(false)} label={item.label} />]
              )}
              <MobileLink to="/contact" onClick={() => setOpen(false)} label="Contact" />
              <Link
                to="/book"
                onClick={() => setOpen(false)}
                data-testid="mobile-cta"
                className="mt-6 bg-wutn text-white font-display uppercase text-2xl px-6 py-4 text-center"
              >
                Book Me To Speak
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}

function MobileLink({ to, label, onClick, indent }) {
  return (
    <NavLink
      to={to}
      onClick={onClick}
      data-testid={`mobile-link-${to === "/" ? "home" : to.slice(1)}`}
      className={({ isActive }) =>
        `font-display uppercase ${indent ? "text-2xl pl-3" : "text-3xl"} py-1.5 transition-colors ${
          isActive ? "text-wutn" : "text-white"
        }`
      }
    >
      {label}
    </NavLink>
  );
}
